import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { StatusBadge, PriorityBadge } from '@/components/common/StatusBadge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Droplets, Plus, Search, MoreHorizontal, Pencil, Trash2, CheckCircle2, Play, Pause, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const STATUSES = ['all', 'scheduled', 'in_progress', 'on_hold', 'completed', 'cancelled'];
const SERVICE_TYPE_VALUES = ['installation', 'repair', 'maintenance', 'inspection', 'replacement'];
const PRIORITY_VALUES = ['low', 'medium', 'high', 'urgent'];

export default function PumpServicePage() {
  const { t } = useTranslation();
  const [services, setServices] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(getEmptyForm());

  function getEmptyForm() {
    return {
      title: '', description: '', type: 'repair', customer_id: '', project_id: '', priority: 'medium', notes: '',
      pump_details: { brand: '', model: '', horsepower: '', voltage: '', serial_number: '', pump_type: '' },
      well_details: { depth_ft: '', casing_diameter: '', static_water_level: '', flow_rate_gpm: '' },
      schedule: { start_date: '', end_date: '', estimated_hours: '' },
      items: [{ description: '', quantity: 1, unit: 'unit', unit_price: 0 }],
      assigned_team: [{ user_id: '', role: 'technician' }],
    };
  }

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      const [svcRes, contRes] = await Promise.all([
        api.get('/pump-service', { params }),
        api.get('/contacts', { params: { page_size: 500 } }),
      ]);
      setServices(svcRes.data);
      setContacts(contRes.data.items || contRes.data);
    } catch { toast.error(t('pumpService.errorLoading')); }
    finally { setLoading(false); }
  }, [statusFilter, search, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => { setEditing(null); setForm(getEmptyForm()); setDialogOpen(true); };

  const openEdit = (svc) => {
    setEditing(svc);
    setForm({
      title: svc.title, description: svc.description || '', type: svc.type || 'repair',
      customer_id: svc.customer_id || '', project_id: svc.project_id || '',
      priority: svc.priority || 'medium', notes: svc.notes || '',
      pump_details: svc.pump_details || { brand: '', model: '', horsepower: '', voltage: '', serial_number: '', pump_type: '' },
      well_details: svc.well_details || { depth_ft: '', casing_diameter: '', static_water_level: '', flow_rate_gpm: '' },
      schedule: svc.schedule || { start_date: '', end_date: '', estimated_hours: '' },
      items: svc.items?.length ? svc.items : [{ description: '', quantity: 1, unit: 'unit', unit_price: 0 }],
      assigned_team: svc.assigned_team?.length ? svc.assigned_team : [{ user_id: '', role: 'technician' }],
    });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error(t('pumpService.titleRequired')); return; }
    try {
      if (editing) {
        await api.put(`/pump-service/${editing.id}`, form);
        toast.success(t('pumpService.serviceUpdated'));
      } else {
        await api.post('/pump-service', form);
        toast.success(t('pumpService.serviceCreated'));
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t('pumpService.errorSaving')); }
  };

  const handleStatusChange = async (id, status) => {
    try { await api.put(`/pump-service/${id}/status`, { status }); toast.success(t('pumpService.statusUpdated')); fetchData(); }
    catch { toast.error(t('pumpService.errorStatus')); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/pump-service/${id}`); toast.success(t('pumpService.serviceDeleted')); fetchData(); }
    catch { toast.error(t('pumpService.errorDeleting')); }
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { description: '', quantity: 1, unit: 'unit', unit_price: 0 }] });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  const updateItem = (idx, field, val) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: field === 'quantity' || field === 'unit_price' ? parseFloat(val) || 0 : val };
    setForm({ ...form, items });
  };

  const getCustomerName = (id) => { const c = contacts.find(c => c.id === id); return c ? `${c.first_name} ${c.last_name}` : '-'; };
  const getTotal = () => form.items.reduce((sum, i) => sum + (i.quantity * i.unit_price), 0);

  return (
    <div data-testid="pump-service-page" className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 font-['Barlow'] tracking-tight flex items-center gap-2">
            <Droplets className="w-5 h-5 sm:w-6 sm:h-6 text-[#3399FF]" /> {t('pumpService.title')}
          </h1>
          <p className="text-sm text-gray-500 mt-1">{t('pumpService.totalServices', { count: services.length })}</p>
        </div>
        <Button data-testid="new-pump-service-btn" onClick={openCreate} className="bg-[#003366] hover:bg-[#004080] text-white shadow-sm">
          <Plus className="w-4 h-4 mr-2" /> {t('pumpService.newService')}
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <TabsList className="bg-gray-100/80 h-9 overflow-x-auto flex-nowrap">
            {STATUSES.map(s => (
              <TabsTrigger key={s} value={s} className="text-xs px-3 data-[state=active]:bg-white whitespace-nowrap">
                {t(`jobs.statuses.${s}`, { defaultValue: s })}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input data-testid="search-pump-service" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('pumpService.searchPlaceholder')} className="pl-10 h-9 bg-white" />
        </div>
      </div>

      <Card className="bg-white border-gray-100 shadow-sm overflow-hidden hidden md:block">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-gray-400">{t('common.loading')}</div>
          ) : services.length === 0 ? (
            <div className="py-16 text-center">
              <Droplets className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 font-medium">{t('pumpService.noPumps')}</p>
              <p className="text-sm text-gray-400 mt-1">{t('pumpService.createFirstPump')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-100">
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">#</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('pumpService.serviceTitle')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('pumpService.serviceType')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('pumpService.customer')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('pumpService.priority')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('bookings.tableHeaders.status')}</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">Total</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((svc) => (
                    <tr key={svc.id} data-testid={`pump-row-${svc.id}`} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors">
                      <td className="px-5 py-3.5 text-sm font-mono text-gray-500">{svc.service_number}</td>
                      <td className="px-5 py-3.5">
                        <p className="text-sm font-medium text-gray-900">{svc.title}</p>
                        {svc.pump_details?.brand && <p className="text-xs text-gray-400">{svc.pump_details.brand} {svc.pump_details.model}</p>}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-600">{t(`pumpService.serviceTypes.${svc.type}`, { defaultValue: svc.type })}</td>
                      <td className="px-5 py-3.5 text-sm text-gray-600">{getCustomerName(svc.customer_id)}</td>
                      <td className="px-5 py-3.5"><PriorityBadge priority={svc.priority} /></td>
                      <td className="px-5 py-3.5"><StatusBadge status={svc.status} /></td>
                      <td className="px-5 py-3.5 text-sm font-bold text-gray-800 text-right">${(svc.total || 0).toLocaleString()}</td>
                      <td className="px-5 py-3.5 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(svc)}><Pencil className="w-3.5 h-3.5 mr-2" />{t('common.edit')}</DropdownMenuItem>
                            {svc.status === 'scheduled' && <DropdownMenuItem onClick={() => handleStatusChange(svc.id, 'in_progress')}><Play className="w-3.5 h-3.5 mr-2 text-blue-500" />{t('pumpService.start')}</DropdownMenuItem>}
                            {svc.status === 'in_progress' && <DropdownMenuItem onClick={() => handleStatusChange(svc.id, 'on_hold')}><Pause className="w-3.5 h-3.5 mr-2 text-amber-500" />{t('pumpService.pause')}</DropdownMenuItem>}
                            {svc.status === 'in_progress' && <DropdownMenuItem onClick={() => handleStatusChange(svc.id, 'completed')}><CheckCircle2 className="w-3.5 h-3.5 mr-2 text-emerald-500" />{t('pumpService.complete')}</DropdownMenuItem>}
                            {svc.status === 'on_hold' && <DropdownMenuItem onClick={() => handleStatusChange(svc.id, 'in_progress')}><Play className="w-3.5 h-3.5 mr-2 text-blue-500" />{t('pumpService.resume')}</DropdownMenuItem>}
                            {svc.status !== 'completed' && svc.status !== 'cancelled' && <DropdownMenuItem onClick={() => handleStatusChange(svc.id, 'cancelled')}><XCircle className="w-3.5 h-3.5 mr-2 text-red-500" />{t('pumpService.cancel')}</DropdownMenuItem>}
                            <DropdownMenuItem onClick={() => handleDelete(svc.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />{t('common.delete')}</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-24 bg-white rounded-xl border border-gray-100 animate-pulse" />)}</div>
        ) : services.length === 0 ? (
          <Card className="bg-white border-gray-100"><CardContent className="py-12 text-center">
            <Droplets className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-gray-500 text-sm font-medium">{t('pumpService.noPumps')}</p>
          </CardContent></Card>
        ) : (
          services.map((svc, idx) => (
            <Card key={svc.id} data-testid={`pump-mobile-${svc.id}`} className="bg-white border-gray-100 shadow-sm mobile-card-enter" style={{ animationDelay: `${idx * 50}ms` }}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{svc.title}</p>
                    <p className="text-xs text-gray-400 font-mono mt-0.5">{svc.service_number}</p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 -mr-2 -mt-1"><MoreHorizontal className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(svc)}><Pencil className="w-3.5 h-3.5 mr-2" />{t('common.edit')}</DropdownMenuItem>
                      {svc.status === 'scheduled' && <DropdownMenuItem onClick={() => handleStatusChange(svc.id, 'in_progress')}><Play className="w-3.5 h-3.5 mr-2 text-blue-500" />{t('pumpService.start')}</DropdownMenuItem>}
                      {svc.status === 'in_progress' && <DropdownMenuItem onClick={() => handleStatusChange(svc.id, 'completed')}><CheckCircle2 className="w-3.5 h-3.5 mr-2 text-emerald-500" />{t('pumpService.complete')}</DropdownMenuItem>}
                      <DropdownMenuItem onClick={() => handleDelete(svc.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />{t('common.delete')}</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <StatusBadge status={svc.status} />
                    <span className="text-xs text-gray-400">{t(`pumpService.serviceTypes.${svc.type}`, { defaultValue: svc.type })}</span>
                  </div>
                  <span className="text-sm font-bold text-gray-800">${(svc.total || 0).toLocaleString()}</span>
                </div>
                {svc.pump_details?.brand && (
                  <p className="text-xs text-gray-500 mt-2">{t('pumpService.brand')}: {svc.pump_details.brand} {svc.pump_details.model} {svc.pump_details.horsepower && `(${svc.pump_details.horsepower} HP)`}</p>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-xl flex items-center gap-2">
              <Droplets className="w-5 h-5 text-[#3399FF]" />
              {editing ? t('pumpService.editService') : t('pumpService.newService')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 space-y-2">
                <Label>{t('pumpService.serviceTitle')} *</Label>
                <Input data-testid="pump-title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder={t('pumpService.serviceTitlePlaceholder')} />
              </div>
              <div className="space-y-2">
                <Label>{t('pumpService.serviceType')}</Label>
                <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
                  <SelectTrigger data-testid="pump-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SERVICE_TYPE_VALUES.map(tv => <SelectItem key={tv} value={tv}>{t(`pumpService.serviceTypes.${tv}`, { defaultValue: tv })}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('pumpService.priority')}</Label>
                <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}>
                  <SelectTrigger data-testid="pump-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITY_VALUES.map(pv => <SelectItem key={pv} value={pv}>{t(`jobs.priorities.${pv}`, { defaultValue: pv })}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('pumpService.customer')}</Label>
                <Select value={form.customer_id} onValueChange={v => setForm({...form, customer_id: v})}>
                  <SelectTrigger data-testid="pump-customer"><SelectValue placeholder={t('pumpService.selectCustomer')} /></SelectTrigger>
                  <SelectContent>
                    {contacts.filter(c => c.type === 'customer' || c.type === 'both').map(c =>
                      <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label>{t('pumpService.description')}</Label>
                <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder={t('pumpService.descriptionPlaceholder')} rows={2} />
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-semibold text-gray-700">{t('pumpService.pumpDetails')}</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Input data-testid="pump-brand" value={form.pump_details.brand} onChange={e => setForm({...form, pump_details: {...form.pump_details, brand: e.target.value}})} placeholder={`${t('pumpService.brand')} (ej: Grundfos)`} />
                <Input data-testid="pump-model" value={form.pump_details.model} onChange={e => setForm({...form, pump_details: {...form.pump_details, model: e.target.value}})} placeholder={t('pumpService.model')} />
                <Input value={form.pump_details.pump_type} onChange={e => setForm({...form, pump_details: {...form.pump_details, pump_type: e.target.value}})} placeholder={`${t('pumpService.pumpType')} (Sumergible, Jet...)`} />
                <Input value={form.pump_details.horsepower} onChange={e => setForm({...form, pump_details: {...form.pump_details, horsepower: e.target.value}})} placeholder={`${t('pumpService.horsepower')} (ej: 1.5)`} type="number" step="any" />
                <Input value={form.pump_details.voltage} onChange={e => setForm({...form, pump_details: {...form.pump_details, voltage: e.target.value}})} placeholder={`${t('pumpService.voltage')} (ej: 230)`} />
                <Input value={form.pump_details.serial_number} onChange={e => setForm({...form, pump_details: {...form.pump_details, serial_number: e.target.value}})} placeholder={t('pumpService.serialNumber')} />
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-semibold text-gray-700">{t('pumpService.wellDetails')}</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Input value={form.well_details.depth_ft} onChange={e => setForm({...form, well_details: {...form.well_details, depth_ft: e.target.value}})} placeholder={t('pumpService.depthFt')} type="number" />
                <Input value={form.well_details.casing_diameter} onChange={e => setForm({...form, well_details: {...form.well_details, casing_diameter: e.target.value}})} placeholder={t('pumpService.casingDiameter')} type="number" step="any" />
                <Input value={form.well_details.static_water_level} onChange={e => setForm({...form, well_details: {...form.well_details, static_water_level: e.target.value}})} placeholder={t('pumpService.staticWaterLevel')} type="number" />
                <Input value={form.well_details.flow_rate_gpm} onChange={e => setForm({...form, well_details: {...form.well_details, flow_rate_gpm: e.target.value}})} placeholder={t('pumpService.flowRateGpm')} type="number" step="any" />
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-semibold text-gray-700">{t('pumpService.schedule')}</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-gray-400">{t('pumpService.startDate')}</Label>
                  <Input type="date" value={form.schedule.start_date} onChange={e => setForm({...form, schedule: {...form.schedule, start_date: e.target.value}})} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-400">{t('pumpService.endDate')}</Label>
                  <Input type="date" value={form.schedule.end_date} onChange={e => setForm({...form, schedule: {...form.schedule, end_date: e.target.value}})} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-400">{t('pumpService.estimatedHours')}</Label>
                  <Input type="number" value={form.schedule.estimated_hours} onChange={e => setForm({...form, schedule: {...form.schedule, estimated_hours: e.target.value}})} placeholder={t('pumpService.hours')} />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-gray-700">{t('pumpService.lineItems')}</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-pump-item">
                  <Plus className="w-3 h-3 mr-1" /> {t('pumpService.addItem')}
                </Button>
              </div>
              <div className="space-y-2">
                {form.items.map((item, idx) => (
                  <div key={idx} className="flex gap-2 items-center p-2 rounded-lg bg-gray-50 border border-gray-100">
                    <Input value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} placeholder={t('pumpService.itemDescription')} className="flex-1 bg-white h-9" />
                    <Input value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} type="number" className="w-20 bg-white h-9 text-center" />
                    <Input value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', e.target.value)} type="number" step="0.01" className="w-24 bg-white h-9" placeholder="$" />
                    <span className="text-sm font-medium text-gray-700 w-20 text-right">${(item.quantity * item.unit_price).toFixed(2)}</span>
                    {form.items.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(idx)} className="h-9 w-9 text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>
                    )}
                  </div>
                ))}
              </div>
              <div className="text-right">
                <span className="text-base font-bold text-[#003366]">Total: ${getTotal().toFixed(2)}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('timecards.notes')}</Label>
              <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button data-testid="save-pump-service-btn" type="submit" className="bg-[#003366] hover:bg-[#004080] text-white">
                {editing ? t('pumpService.saveChanges') : t('pumpService.createService')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
